import { useEffect, useState } from 'react';

export type UserRole = 'student' | 'tutor' | 'admin';

export interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  avatarUrl?: string;
  verified?: boolean;
  rating?: number;
  totalSessions?: number;
  hourlyRate?: number;
}

function normalizeRole(role: unknown): UserRole {
  const normalized = typeof role === 'string' ? role.toLowerCase() : '';

  if (normalized === 'tutor' || normalized === 'student' || normalized === 'admin') {
    return normalized;
  }

  return 'student';
}

function normalizeCurrentUser(user: CurrentUser): CurrentUser {
  return {
    ...user,
    role: normalizeRole(user.role),
  };
}

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        
        if (accessToken) {
          setIsAuthenticated(true);
          
          // Try to fetch full user profile
          try {
            const response = await fetch('/api/users/profile', {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data) {
                if (data.data.user) {
                  setUser(normalizeCurrentUser(data.data.user));
                } else {
                  const storedUser = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser');
                  if (storedUser) {
                    setUser(normalizeCurrentUser(JSON.parse(storedUser)));
                  }
                }
              }
            } else {
              // Fall back to stored currentUser if profile fetch fails
              const storedUser = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser');
              if (storedUser) {
                setUser(normalizeCurrentUser(JSON.parse(storedUser)));
              }
            }
          } catch (err) {
            // Silent fail - use stored user data if available
            const storedUser = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser');
            if (storedUser) {
              setUser(normalizeCurrentUser(JSON.parse(storedUser)));
            }
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Auth initialization failed');
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const clearAuthStorage = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('currentUser');
  };

  const logout = async () => {
    const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

    try {
      if (accessToken && refreshToken) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch {
      // Even if server logout fails, always clear client auth state.
    } finally {
      clearAuthStorage();
      setIsAuthenticated(false);
      setUser(null);
      // Hard redirect: clears all in-memory React Query cache, React state,
      // and any other session data, then lands on the main page.
      window.location.replace('/');
    }
  };

  return { isAuthenticated, isLoading, user, error, logout };
}

export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
}

export function getCurrentUser(): CurrentUser | null {
  const stored = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser');
  return stored ? normalizeCurrentUser(JSON.parse(stored)) : null;
}
